import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { 
  Clock, 
  User,
  CalendarDays,
  Plus,
  Trash2,
  Bell,
  CheckCircle,
  AlertCircle,
  Users,
  Play,
  Pause,
  Phone,
  MessageSquare,
  Timer,
  UserPlus,
  History,
  TrendingUp,
  XCircle,
  ChevronRight,
  Sparkles
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const WaitlistManager = ({ token, stylists = [], services = [], isClient = true }) => {
  const [activeTab, setActiveTab] = useState(isClient ? "waitlist" : "walkin");
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [walkinQueue, setWalkinQueue] = useState({ queue: [], total_waiting: 0, current_client: null, completed_today: 0, no_shows_today: 0 });
  const [walkinHistory, setWalkinHistory] = useState({ entries: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [showAddWalkin, setShowAddWalkin] = useState(false);
  
  // Waitlist form
  const [formData, setFormData] = useState({
    stylist_id: "",
    service_id: "",
    preferred_date: "",
    preferred_time_range: "morning",
    notes: ""
  });
  
  // Walk-in form
  const [walkinForm, setWalkinForm] = useState({
    client_name: "",
    client_phone: "",
    service_id: "",
    service_name: "",
    estimated_duration: 30,
    notes: ""
  });

  const TIME_RANGES = [
    { value: "morning", label: "Morning (9AM - 12PM)" },
    { value: "afternoon", label: "Afternoon (12PM - 5PM)" },
    { value: "evening", label: "Evening (5PM - 8PM)" },
    { value: "any", label: "Any Time" }
  ];

  const DURATION_OPTIONS = [
    { value: 15, label: "15 min - Quick Trim" },
    { value: 30, label: "30 min - Standard Cut" },
    { value: 45, label: "45 min - Cut + Style" },
    { value: 60, label: "60 min - Full Service" },
    { value: 90, label: "90 min - Complex Style" },
    { value: 120, label: "120 min - Color/Treatment" }
  ];

  // Fetch data
  const fetchWaitlist = useCallback(async () => {
    try {
      const endpoint = isClient ? `${API}/waitlist/my` : `${API}/waitlist/stylist`;
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWaitlistEntries(response.data);
    } catch (error) {
      console.error("Failed to fetch waitlist:", error);
    }
  }, [token, isClient]);

  const fetchWalkinQueue = useCallback(async () => {
    if (isClient) return;
    try {
      const response = await axios.get(`${API}/waitlist/walkin/queue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWalkinQueue(response.data);
    } catch (error) {
      console.error("Failed to fetch walk-in queue:", error);
    }
  }, [token, isClient]);

  const fetchWalkinHistory = useCallback(async () => {
    if (isClient) return;
    try {
      const response = await axios.get(`${API}/waitlist/walkin/history?days=7`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWalkinHistory(response.data);
    } catch (error) {
      console.error("Failed to fetch history:", error);
    }
  }, [token, isClient]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchWaitlist(), fetchWalkinQueue(), fetchWalkinHistory()]);
      setLoading(false);
    };
    loadData();
    
    // Auto-refresh queue every 30 seconds for stylists
    if (!isClient) {
      const interval = setInterval(() => {
        fetchWalkinQueue();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [fetchWaitlist, fetchWalkinQueue, fetchWalkinHistory, isClient]);

  // Waitlist actions
  const joinWaitlist = async () => {
    if (!formData.stylist_id || !formData.preferred_date) {
      toast.error("Please select a stylist and preferred date");
      return;
    }

    try {
      await axios.post(`${API}/waitlist/`, {
        ...formData,
        preferred_dates: [formData.preferred_date],
        preferred_times: [formData.preferred_time_range]
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("You've been added to the waitlist! We'll notify you when a slot opens.");
      setShowJoinForm(false);
      setFormData({ stylist_id: "", service_id: "", preferred_date: "", preferred_time_range: "morning", notes: "" });
      fetchWaitlist();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join waitlist");
    }
  };

  const leaveWaitlist = async (entryId) => {
    try {
      await axios.delete(`${API}/waitlist/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Removed from waitlist");
      fetchWaitlist();
    } catch (error) {
      toast.error("Failed to leave waitlist");
    }
  };

  const notifyWaitlistClient = async (entryId) => {
    try {
      await axios.post(`${API}/waitlist/${entryId}/notify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Client notified! They'll receive an email with booking link.");
      fetchWaitlist();
    } catch (error) {
      toast.error("Failed to notify client");
    }
  };

  // Walk-in actions
  const addWalkin = async () => {
    if (!walkinForm.client_name) {
      toast.error("Please enter client name");
      return;
    }

    try {
      const response = await axios.post(`${API}/waitlist/walkin`, {
        ...walkinForm,
        stylist_id: "current_user" // Will be set by backend
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setShowAddWalkin(false);
      setWalkinForm({ client_name: "", client_phone: "", service_id: "", service_name: "", estimated_duration: 30, notes: "" });
      fetchWalkinQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add walk-in");
    }
  };

  const updateWalkinStatus = async (walkinId, status) => {
    try {
      await axios.put(`${API}/waitlist/walkin/${walkinId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const statusMessages = {
        in_progress: "Client started! Timer running.",
        completed: "Service completed!",
        no_show: "Marked as no-show"
      };
      
      toast.success(statusMessages[status] || "Status updated");
      fetchWalkinQueue();
      fetchWalkinHistory();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const notifyWalkin = async (walkinId) => {
    try {
      const response = await axios.post(`${API}/waitlist/walkin/${walkinId}/notify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.sent) {
        toast.success("SMS sent to client!");
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to send notification");
    }
  };

  const removeWalkin = async (walkinId) => {
    try {
      await axios.delete(`${API}/waitlist/walkin/${walkinId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Removed from queue");
      fetchWalkinQueue();
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  const formatWaitTime = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  if (loading) {
    return (
      <Card className="bg-stone-800/50 border-stone-700">
        <CardContent className="p-8 text-center">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading queue...</p>
        </CardContent>
      </Card>
    );
  }

  // CLIENT VIEW
  if (isClient) {
    return (
      <div className="space-y-6">
        <Card className="bg-stone-800/50 border-stone-700">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-400" />
                My Waitlist
              </CardTitle>
              <Button onClick={() => setShowJoinForm(true)} className="bg-amber-500 hover:bg-amber-600 text-black">
                <Plus className="w-4 h-4 mr-2" />
                Join Waitlist
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {waitlistEntries.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 text-stone-600 mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-2">Not on Any Waitlists</h3>
                <p className="text-gray-400 text-sm mb-4">Join a waitlist to get notified when your preferred time opens up</p>
                <Button onClick={() => setShowJoinForm(true)} variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10">
                  <Plus className="w-4 h-4 mr-2" />
                  Join a Waitlist
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {waitlistEntries.map((entry) => (
                  <div key={entry.waitlist_id} className="p-4 bg-stone-900 rounded-lg border border-stone-700">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-white font-medium">{entry.stylist_name || "Stylist"}</p>
                        <p className="text-sm text-gray-400">
                          Preferred: {entry.preferred_dates?.join(", ") || entry.preferred_date}
                        </p>
                        <Badge className={`mt-2 ${
                          entry.status === "notified" ? "bg-green-500/20 text-green-400" :
                          entry.status === "expired" ? "bg-red-500/20 text-red-400" :
                          "bg-amber-500/20 text-amber-400"
                        }`}>
                          {entry.status === "notified" ? "Slot Available!" : entry.status}
                        </Badge>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => leaveWaitlist(entry.waitlist_id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Join Waitlist Dialog */}
        <Dialog open={showJoinForm} onOpenChange={setShowJoinForm}>
          <DialogContent className="bg-stone-900 border-stone-700 text-white">
            <DialogHeader>
              <DialogTitle>Join Waitlist</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-gray-300">Select Stylist</Label>
                <Select value={formData.stylist_id} onValueChange={(v) => setFormData({ ...formData, stylist_id: v })}>
                  <SelectTrigger className="bg-stone-800 border-stone-600 text-white">
                    <SelectValue placeholder="Choose a stylist" />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-600">
                    {stylists.map(s => (
                      <SelectItem key={s.user_id} value={s.user_id}>{s.user?.name || s.business_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Preferred Date</Label>
                <Input 
                  type="date" 
                  value={formData.preferred_date}
                  onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                  className="bg-stone-800 border-stone-600 text-white"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Label className="text-gray-300">Preferred Time</Label>
                <Select value={formData.preferred_time_range} onValueChange={(v) => setFormData({ ...formData, preferred_time_range: v })}>
                  <SelectTrigger className="bg-stone-800 border-stone-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-600">
                    {TIME_RANGES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Notes (optional)</Label>
                <Input 
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any special requests..."
                  className="bg-stone-800 border-stone-600 text-white"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowJoinForm(false)} className="border-stone-600 text-gray-300">Cancel</Button>
              <Button onClick={joinWaitlist} className="bg-amber-500 hover:bg-amber-600 text-black">Join Waitlist</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // STYLIST VIEW
  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-stone-800 border-stone-700">
          <TabsTrigger value="walkin" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <Users className="w-4 h-4 mr-2" />
            Walk-in Queue
            {walkinQueue.total_waiting > 0 && (
              <Badge className="ml-2 bg-red-500 text-white">{walkinQueue.total_waiting}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="waitlist" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <Clock className="w-4 h-4 mr-2" />
            Future Waitlist
            {waitlistEntries.filter(e => e.status === "active").length > 0 && (
              <Badge className="ml-2 bg-blue-500 text-white">{waitlistEntries.filter(e => e.status === "active").length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <History className="w-4 h-4 mr-2" />
            History
          </TabsTrigger>
        </TabsList>

        {/* WALK-IN QUEUE TAB */}
        <TabsContent value="walkin" className="mt-6">
          {/* Stats Bar */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card className="bg-stone-800/50 border-stone-700">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-amber-400">{walkinQueue.total_waiting}</p>
                <p className="text-xs text-gray-400">In Queue</p>
              </CardContent>
            </Card>
            <Card className="bg-stone-800/50 border-stone-700">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-400">{walkinQueue.completed_today}</p>
                <p className="text-xs text-gray-400">Completed Today</p>
              </CardContent>
            </Card>
            <Card className="bg-stone-800/50 border-stone-700">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-400">{walkinQueue.no_shows_today}</p>
                <p className="text-xs text-gray-400">No Shows</p>
              </CardContent>
            </Card>
            <Card className="bg-stone-800/50 border-stone-700">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-400">{formatWaitTime(walkinQueue.total_estimated_wait || 0)}</p>
                <p className="text-xs text-gray-400">Total Wait</p>
              </CardContent>
            </Card>
          </div>

          {/* Current Client */}
          {walkinQueue.current_client && (
            <Card className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/50 mb-6">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                      <Play className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-bold text-lg">{walkinQueue.current_client.client_name}</p>
                      <p className="text-green-400 text-sm">IN PROGRESS - {walkinQueue.current_client.service_name || "Service"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-500/20 text-green-400">
                      <Timer className="w-3 h-3 mr-1" />
                      ~{walkinQueue.current_client.estimated_duration} min
                    </Badge>
                    <Button 
                      onClick={() => updateWalkinStatus(walkinQueue.current_client.walkin_id, "completed")}
                      className="bg-green-500 hover:bg-green-600 text-white"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Complete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Add Walk-in Button */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-white font-semibold">Waiting Queue</h3>
            <Button onClick={() => setShowAddWalkin(true)} className="bg-amber-500 hover:bg-amber-600 text-black">
              <UserPlus className="w-4 h-4 mr-2" />
              Add Walk-in
            </Button>
          </div>

          {/* Queue List */}
          {walkinQueue.queue.filter(e => e.status === "waiting").length === 0 ? (
            <Card className="bg-stone-800/50 border-stone-700">
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-stone-600 mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-2">No One Waiting</h3>
                <p className="text-gray-400 text-sm">Add walk-in clients as they arrive</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {walkinQueue.queue.filter(e => e.status === "waiting").map((entry, idx) => (
                <Card key={entry.walkin_id} className="bg-stone-800/50 border-stone-700 hover:border-amber-500/50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                          <span className="text-amber-400 font-bold">#{idx + 1}</span>
                        </div>
                        <div>
                          <p className="text-white font-medium">{entry.client_name}</p>
                          <div className="flex items-center gap-3 text-sm text-gray-400">
                            <span>{entry.service_name || "Walk-in"}</span>
                            <span>•</span>
                            <span>~{entry.estimated_duration} min</span>
                            {entry.client_phone && (
                              <>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {entry.client_phone}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-500/20 text-blue-400">
                          <Clock className="w-3 h-3 mr-1" />
                          ~{entry.estimated_wait_minutes || 0} min wait
                        </Badge>
                        
                        {entry.client_phone && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => notifyWalkin(entry.walkin_id)}
                            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                          >
                            <MessageSquare className="w-4 h-4" />
                          </Button>
                        )}
                        
                        <Button 
                          size="sm" 
                          onClick={() => updateWalkinStatus(entry.walkin_id, "in_progress")}
                          className="bg-green-500 hover:bg-green-600 text-white"
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Start
                        </Button>
                        
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => updateWalkinStatus(entry.walkin_id, "no_show")}
                          className="text-red-400 hover:bg-red-500/10"
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* FUTURE WAITLIST TAB */}
        <TabsContent value="waitlist" className="mt-6">
          <Card className="bg-stone-800/50 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-400" />
                Clients Waiting for Future Slots
              </CardTitle>
            </CardHeader>
            <CardContent>
              {waitlistEntries.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 text-stone-600 mx-auto mb-3" />
                  <h3 className="text-white font-semibold mb-2">No Waitlist Entries</h3>
                  <p className="text-gray-400 text-sm">Clients can join your waitlist when you&apos;re fully booked</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {waitlistEntries.map((entry) => (
                    <div key={entry.waitlist_id} className="p-4 bg-stone-900 rounded-lg border border-stone-700">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-white font-medium">{entry.client_name || entry.client_email}</p>
                          <p className="text-sm text-gray-400">
                            Wants: {entry.preferred_dates?.join(", ") || "Any date"} - {entry.preferred_times?.join(", ") || "Any time"}
                          </p>
                          {entry.notes && (
                            <p className="text-sm text-gray-500 mt-1">&quot;{entry.notes}&quot;</p>
                          )}
                          <Badge className={`mt-2 ${
                            entry.status === "notified" ? "bg-green-500/20 text-green-400" : "bg-amber-500/20 text-amber-400"
                          }`}>
                            {entry.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          {entry.status === "active" && (
                            <Button 
                              size="sm"
                              onClick={() => notifyWaitlistClient(entry.waitlist_id)}
                              className="bg-green-500 hover:bg-green-600 text-white"
                            >
                              <Bell className="w-4 h-4 mr-1" />
                              Notify Available
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* HISTORY TAB */}
        <TabsContent value="history" className="mt-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card className="bg-stone-800/50 border-stone-700">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-white">{walkinHistory.stats.total || 0}</p>
                <p className="text-xs text-gray-400">Total (7 days)</p>
              </CardContent>
            </Card>
            <Card className="bg-stone-800/50 border-stone-700">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-400">{walkinHistory.stats.completed || 0}</p>
                <p className="text-xs text-gray-400">Completed</p>
              </CardContent>
            </Card>
            <Card className="bg-stone-800/50 border-stone-700">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-400">{walkinHistory.stats.no_show_rate || 0}%</p>
                <p className="text-xs text-gray-400">No-Show Rate</p>
              </CardContent>
            </Card>
            <Card className="bg-stone-800/50 border-stone-700">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-400">{walkinHistory.stats.average_wait_minutes || 0} min</p>
                <p className="text-xs text-gray-400">Avg Wait Time</p>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-stone-800/50 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white">Recent Walk-ins (7 days)</CardTitle>
            </CardHeader>
            <CardContent>
              {walkinHistory.entries?.length === 0 ? (
                <p className="text-gray-400 text-center py-4">No history yet</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {walkinHistory.entries?.slice(0, 20).map((entry) => (
                    <div key={entry.walkin_id} className="flex items-center justify-between p-3 bg-stone-900 rounded-lg">
                      <div>
                        <p className="text-white text-sm">{entry.client_name}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(entry.check_in_time).toLocaleDateString()} - {entry.service_name || "Walk-in"}
                        </p>
                      </div>
                      <Badge className={
                        entry.status === "completed" ? "bg-green-500/20 text-green-400" :
                        entry.status === "no_show" ? "bg-red-500/20 text-red-400" :
                        "bg-gray-500/20 text-gray-400"
                      }>
                        {entry.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Walk-in Dialog */}
      <Dialog open={showAddWalkin} onOpenChange={setShowAddWalkin}>
        <DialogContent className="bg-stone-900 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-amber-400" />
              Add Walk-in Client
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300">Client Name *</Label>
              <Input 
                value={walkinForm.client_name}
                onChange={(e) => setWalkinForm({ ...walkinForm, client_name: e.target.value })}
                placeholder="Enter client name"
                className="bg-stone-800 border-stone-600 text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300">Phone Number (for SMS updates)</Label>
              <Input 
                value={walkinForm.client_phone}
                onChange={(e) => setWalkinForm({ ...walkinForm, client_phone: e.target.value })}
                placeholder="(555) 123-4567"
                className="bg-stone-800 border-stone-600 text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300">Service</Label>
              <Input 
                value={walkinForm.service_name}
                onChange={(e) => setWalkinForm({ ...walkinForm, service_name: e.target.value })}
                placeholder="e.g., Haircut, Fade, etc."
                className="bg-stone-800 border-stone-600 text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300">Estimated Duration</Label>
              <Select 
                value={walkinForm.estimated_duration.toString()} 
                onValueChange={(v) => setWalkinForm({ ...walkinForm, estimated_duration: parseInt(v) })}
              >
                <SelectTrigger className="bg-stone-800 border-stone-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-stone-800 border-stone-600">
                  {DURATION_OPTIONS.map(d => (
                    <SelectItem key={d.value} value={d.value.toString()}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300">Notes</Label>
              <Input 
                value={walkinForm.notes}
                onChange={(e) => setWalkinForm({ ...walkinForm, notes: e.target.value })}
                placeholder="Any special requests..."
                className="bg-stone-800 border-stone-600 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddWalkin(false)} className="border-stone-600 text-gray-300">
              Cancel
            </Button>
            <Button onClick={addWalkin} className="bg-amber-500 hover:bg-amber-600 text-black">
              <UserPlus className="w-4 h-4 mr-2" />
              Add to Queue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WaitlistManager;
